import { createElement, useState, useEffect, forwardRef, useCallback, useRef } from 'react';
import { Icon } from '../Icon';
import { AvatarProps, AvatarSize, AvatarVariant } from './types';
import { postfixTestId } from '../../util/testId';
import { useDesignTokens, DesignTokens, renderInlineCssVars } from '../../theme';
import { SVGIconDescriptor } from '../SVG';
import { AvatarIndividual, AvatarGroup } from '@sas/react-svg';
import { useAvatarA11yInfo } from './__internal__/useAvatarA11yInfo';
import { getLogger } from '@sas/nova-core/logging';
import { useOnAvatarUpdate } from './__internal__/useOnAvatarUpdate';
import { markEvent } from '../../marked-events';
import { getAvatarDataProvider } from './__internal__/AvatarDataProvider';
import { UserType } from '@sas/nova-core/services';
import cx from 'classnames';
import cn from './Avatar.css.json';
import './Avatar.css';

const ICON_TYPES: { [type in UserType]: SVGIconDescriptor } = {
  user: AvatarIndividual,
  group: AvatarGroup,
};

const logger = getLogger('Avatar');

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  { dataProvider = getAvatarDataProvider(), ...props },
  ref
) {
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const { ariaLabel, tooltip, name } = useAvatarA11yInfo({ ...props, dataProvider });
  const [fetchedTime, setFetchedTime] = useState<number>();
  const [avatarSrc, setAvatarSrc] = useState<string>();
  const fetchCounter = useRef(0);

  const size: AvatarSize = props.size ?? 'medium';
  const variant: AvatarVariant = props.variant || 'primary';

  const avatarIcon = (
    <Icon
      className={cn.avatarIcon}
      icon={ICON_TYPES[props.type ?? 'user']}
      tooltip=""
      testId={postfixTestId(props.testId, 'icon')}
    />
  );

  const avatarImage = avatarSrc ? (
    <img
      className={cx(cn.avatarImage, !isImageVisible && cn.imageInvisible)}
      data-testid={postfixTestId(props.testId, 'image')}
      src={avatarSrc}
      alt=""
      onError={() => setIsImageVisible(false)}
      onLoad={() => setIsImageVisible(true)}
    />
  ) : null;

  let avatar: JSX.Element | null;
  if (props.type === 'group') {
    avatar = avatarImage ?? avatarIcon;
  } else {
    if (name === undefined || avatarSrc === undefined) {
      // Still waiting for responses from Identities Services
      avatar = null;
    } else if (avatarImage) {
      avatar = avatarImage;
    } else if (name && name.length > 0) {
      // codePointAt will not return undefined because name has length > 1
      avatar = <div>{String.fromCodePoint(name.codePointAt(0)!)}</div>;
    } else {
      avatar = avatarIcon;
    }
  }

  useEffect(() => {
    return () => {
      // Increment the fetchCounter to discard the fetch response after unmounting
      fetchCounter.current += 1;
    };
  }, []);

  const updateShowCustomImage = useCallback(() => {
    const fetchCount = ++fetchCounter.current;
    dataProvider
      .getAvatar?.(props.userId, props.type)
      .then((src) => {
        if (fetchCounter.current > fetchCount) return;
        setAvatarSrc(src);
      })
      .catch(() => {
        if (fetchCounter.current > fetchCount) return;
        setAvatarSrc('');
        logger.error(
          `Failed to fetch the avatar details info for id:${props.userId} and type: ${props.type}`
        );
      })
      .finally(() => {
        if (fetchCounter.current > fetchCount) return;
        setFetchedTime(Date.now());
      });
  }, [dataProvider, props.userId, props.type]);

  useOnAvatarUpdate(dataProvider, updateShowCustomImage, props.userId, props.type, fetchedTime);

  useEffect(updateShowCustomImage, [updateShowCustomImage]);

  // Get design tokens
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars(getAvatarTokens(dt), 'avatar'),
    ...renderInlineCssVars(getAvatarFocusTokens(dt, variant)),
  };

  return (
    <div
      id={props.id}
      ref={ref}
      data-testid={props.testId}
      className={cx(cn[size], cn.avatar, props.className)}
      style={{ ...tokens, ...props.style }}
      aria-label={ariaLabel}
      aria-haspopup={props.ariaHasPopup}
      aria-hidden={props.ariaLabel === '' ? true : undefined}
      title={tooltip}
      role={props.useAsButton ? 'button' : 'img'}
      tabIndex={
        props.useAsButton && props.focusable !== false
          ? 0
          : // Set to -1 if focusable is explicitly set to false.
          // This is to make sure current focus does not get lost in InnerToolbar
          props.focusable === false
          ? -1
          : undefined
      }
      onClick={() => {
        props.useAsButton && props.onPress?.();
      }}
      onKeyDown={(e) => {
        if (props.useAsButton && (e.key === 'Enter' || e.key === ' ')) {
          markEvent(e);
          setIsActive(true);
        }
      }}
      onKeyUp={(e) => {
        if (props.useAsButton && (e.key === 'Enter' || e.key === ' ')) {
          setIsActive(false);
        }
      }}
      onKeyPress={(e) => {
        if (props.useAsButton && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          props.onPress?.();
        }
      }}
      onBlur={() => setIsActive(false)}
    >
      <div
        className={cx(
          cn.innerContainer,
          avatar?.type === 'img' && cn.hasImage,
          props.useAsButton && cn.avatarButton,
          isActive && cn.active
        )}
        aria-hidden="true"
      >
        {avatar}
      </div>
    </div>
  );
});

const getAvatarTokens = (dt: DesignTokens) => {
  const avatarFontTokens = {
    'font-size-m': dt.fontNova.size.m,
    'font-size-xl': dt.fontNova.size.xl,
    'font-weight-thick': dt.fontNova.weight.thick,
    'text-transform-uppercase': dt.fontNova.textTransform.uppercase,
  };
  switch (dt.themeVariant) {
    case 'high-contrast':
    case 'dark':
      return {
        bg: dt.statePrimaryNova.normal.bg,
        'hover-bg': dt.statePrimaryNova.normal.bg,
        'hover-fg': dt.statePrimaryNova.normal.fg,
        'active-bg': dt.statePrimaryNova.active.bg,
        'active-fg': dt.statePrimaryNova.active.fg,
        bd: dt.statePrimaryNova.normal.bd,
        fg: dt.c.colorTextPrimary,
        ...avatarFontTokens,
      };
    default:
      return {
        bg: dt.statePrimaryNova.normal.bg,
        'hover-bg': dt.statePrimaryNova.hover.bg,
        'hover-fg': dt.c.colorTextInverse,
        'active-bg': dt.statePrimaryNova.active.bg,
        'active-fg': dt.statePrimaryNova.active.fg,
        bd: dt.c.colorUiPop,
        fg: dt.c.colorTextInverse,
        ...avatarFontTokens,
      };
  }
};

const getAvatarFocusTokens = (dt: DesignTokens, variant: AvatarVariant) => {
  switch (dt.themeVariant) {
    case 'high-contrast':
    case 'dark':
      return {
        'avatar-focus': 'var(--focus-light-bg)',
      };
    default:
      return {
        'avatar-focus': variant === 'primary' ? 'var(--focus-light-bg)' : 'var(--focus-dark-bg)',
      };
  }
};
